<template lang="pug">
div#app(
    v-bind:key="updateKey")

    navigation(
        v-bind:is-authenticated="isAuthenticated")

    //- main contents from router-view
    div.container
        keep-alive(
            v-bind:include="keepAliveInclude")
            router-view

        site-footer


    //- overlays
    //----------------

    //- initial GDPR notification
    gdpr-notification(
        v-if="!isGdprAccepted")

    //- slideshow, controlled with signals from multi-stack
    slideshow-ctrl

    //- index search overlay covering navigation
    search-overlay

    //- back-to-top button
    back-to-top


    //- modals
    //----------------

    //- terms of service
    modal-terms-of-service(
        v-if="isAuthenticated"
        ref="modalTermsOfService"
        v-bind:show="showModalTermsOfService"
        v-bind:accept-button="true"
        v-on:accept="onAcceptTermsOfService"
        v-on:close="showModalTermsOfService = false")

    //- warn of unsaved changes in buffer while moving away from the page
    modal-unsaved-changes(
        v-if="isAuthenticated")

    //- save operation pending (is not actually a modal)
    modal-save-pending(
        v-if="isAuthenticated")

    //- something went wrong
    modal-error-message(
        v-if="error"
        v-bind:error="error"
        v-on:close="error = null")

    //- some action requires session
    modal-login-required(
        v-if="!isAuthenticated")

    //- session has expired
    modal-login-expired(
        v-if="!isAuthenticated")

    //- login prompts
    //- (check if this is needed, it acts as modal-login-required)
    anon-login-prompt(
        v-if="!isAuthenticated"
        ref="anonLoginPrompt")

</template>

<script>
import UserSessionMixin from '@/components/Base/UserSessionMixin';
import AppLocalisationMixin from '@/components/Base/AppLocalisationMixin';
import {
    phaseAnonymous,
    phaseExpired,
    phaseAuthenticated,
    phaseProfile,
    phaseComplete
} from '@/store/ux';

const keepAliveComponents = [
    'actor-page',
    'actors-index',
    'article-page',
    'articles-index',
    'artwork-page',
    'artworks-index',
    'audio-page',
    'audios-index',
    'collection-page',
    'collections-index',
    'event-page',
    'events-index',
    'galleries-index',
    'organization-page',
    'organizations-index',
    'privacy-policy',
    'terms-of-service',
    'video-page',
    'videos-index'
];

export default {
    name: 'app',

    mixins: [ UserSessionMixin, AppLocalisationMixin ],

    components: {
        Navigation: () => import('@/components/Navigation'),
        SiteFooter: () => import('@/components/SiteFooter'),
        BackToTop: () => import('@/components/BackToTop'),
        SlideshowCtrl: () => import('@/components/SlideshowCtrl'),
        SearchOverlay: () => import('@/components/SearchOverlay'),
        ModalLoginExpired: () => import('@/components/Modals/LoginExpired'),
        ModalLoginRequired: () => import('@/components/Modals/LoginRequired'),
        AnonLoginPrompt: () => import('@/components/AnonLoginPrompt'),
        ModalTermsOfService: () => import('@/components/Modals/TermsOfService'),
        ModalUnsavedChanges: () => import('@/components/Modals/UnsavedChanges'),
        ModalSavePending: () => import('@/components/Modals/SavePending'),
        ModalErrorMessage: () => import('@/components/Modals/ErrorMessage'),
        GdprNotification: () => import('@/components/GdprNotification')
    },

    data: function () {
        return {
            hasInitialSession: false,
            isAuthenticated: false,
            isGdprAccepted: false,
            initItem: null,
            updateKey: 0,
            showModalTermsOfService: false,
            debouncedDetectRoute: _.debounce(this.detectRoute, 150, {trailing: true}), // enable pages without session check
            debouncedResetKeepAlive: _.debounce(this.resetKeepAlive, 1000, {trailing: true}),
            keepAliveInclude: _.clone(keepAliveComponents),
            ready: false,
            error: null
        };
    },

    created: function () {
        this.checkInitItemLocalUrl(); // will possibly redirect by InitItem LocalUrl
    },

    beforeMount: function () {
        this.setTheme();
        this.initBusEvents();

        this.isGdprAccepted = !!this.getGdprCookie();

        // Page Visibility API to check user session after long absence on show, and clear keep-alive cache on hide
        window.addEventListener("visibilitychange", this.onVisibilityChange);

        // Event Listener for Iframe
        window.addEventListener("message", this.iframeEvent, false);
    },

    mounted: function () {
        this.debouncedDetectRoute(); // runs once for mount & initial route change
    },

    beforeDestroy: function () {
        this.clearBusEvents();
        this.debouncedDetectRoute.cancel();

        window.removeEventListener("visibilitychange", this.onVisibilityChange);
        window.removeEventListener("message", this.iframeEvent);
    },

    watch: {
        /**
         * Check every route change.
         */
        $route: function () {
            this.debouncedDetectRoute();
        }
    },

    methods: {

        iframeEvent: function (event) {
            // Verify App Domain
            // if(event.origin !== 'https://www.xyz.com') return; {name: "clickLogin"
            //  path: '/register/info',
            //    name: 'RegistrationInfo',
            if (event.data && event.data && event.data.data) {
                if (event.data.data.name === "clickLogin") {
                    this.$eventBus.$emit('anon-login-required');
                }
            }
        },

        /**
         * Global event bus events.
         */
        initBusEvents: function () {
            this.$eventBus.$on('use-profile-change', this.onUseProfileChange);
            this.$eventBus.$on('edit-mode-change', this.onUpdate);

            // user auth signals
            this.$eventBus.$on('auth-change', this.onAuthChange);
            this.$eventBus.$on('user-login', this.onLogin); // from UserSessionMixin
            this.$eventBus.$on('user-logout', this.onLogout); // from UserSessionMixin

            // page status signals
            this.$eventBus.$on('page-ready', this.onReady);
            this.$eventBus.$on('page-update', this.onUpdate);
            this.$eventBus.$on('page-refresh', this.onRefresh);
            this.$eventBus.$on('page-error', this.onError);

            // app specific signals
            this.$eventBus.$on('app-theme-change', this.setTheme);
            this.$eventBus.$on('app-open-terms-modal', this.onOpenTermsModal);
        },

        clearBusEvents: function () {
            this.$eventBus.$off('use-profile-change', this.onUseProfileChange);
            this.$eventBus.$off('edit-mode-change', this.onUpdate);

            this.$eventBus.$off('auth-change', this.onAuthChange);
            this.$eventBus.$off('user-login', this.onLogin);
            this.$eventBus.$off('user-logout', this.onLogout);

            this.$eventBus.$off('page-ready', this.onReady);
            this.$eventBus.$off('page-update', this.onUpdate);
            this.$eventBus.$off('page-refresh', this.onRefresh);
            this.$eventBus.$off('page-error', this.onError);

            this.$eventBus.$off('app-theme-change', this.setTheme);
            this.$eventBus.$off('app-open-terms-modal', this.onOpenTermsModal);
        },

        /**
         * Get user session.
         * Page load will always fetch hello.
         */
        initUserSession: function () {

            // force fetch
            const force = true;
            return this.getUserSession(force)
                .then(async (userInfo) => {

                    // check current uiTag and clear the store if needed
                    if (force && userInfo?.CurrentUiTag) {
                        if (!await this.checkUiTag(userInfo.CurrentUiTag)) {
                            return false;
                        }
                    }

                    // start loading store contents on next tick,
                    // the store should keep the response cached for some while
                    this.$nextTick(this.fetchUserProfileData);

                    return userInfo;
                })
                .catch((error) => {
                    if (error.message === 'session expired') {
                        // console.log("App: session has expired!")

                        if (this.$store.getters['ux/getLoginPhase'] > phaseExpired) {
                            this.$store.commit('ux/setLoginPhase', phaseExpired);
                        }

                        // LoginButton detects change in `this.isAuthenticated` and notifies the message.
                    }
                    else {
                        console.warn(error);
                    }
                })
                .finally(() => {
                    this.checkAuthenticationState();

                    if (!this.ready) {
                        this.closeSplash();
                        this.ready = true;
                    }
                });
        },

        /**
         * Check Session authentication, and update login phase values in store.
         * `isUserSessionAuthenticated()` uses stored session data.
         *
         * NOTE: This system could be improved. `userSession` could be watched
         *       to get `this.isAuthenticated` state to react to changes in stored session.
         *       This data is passed on in props to multiple child components.
         */
        checkAuthenticationState: function () {
            this.isAuthenticated = this.isUserSessionAuthenticated();

            // if not authenticated, set login phase to anonymous unless it is expired
            if (!this.isAuthenticated && this.$store.getters['ux/getLoginPhase'] > phaseExpired) {
                this.$store.commit('ux/setLoginPhase', phaseAnonymous);
            }

            // set login phase minimum to authenticated
            if (this.isAuthenticated && this.$store.getters['ux/getLoginPhase'] < phaseAuthenticated) {
                this.$store.commit('ux/setLoginPhase', phaseAuthenticated);
            }
        },

        /**
         * Detect `window.TAIDEART.getInitItem` and possibly redirect.
         *
         *  NOTE: the item must be mocked in localhost development;
         *        it does not appear just by navigating to slug url,
         *        as it does when build is served on the backend.
         *
            window.TAIDEART.getInitItem = function () {
                return {
                    LocalUrl: '/articles',
                    PreferredLocale: 'sv'
                };
            };
        */
        checkInitItemLocalUrl: function () {
            if (window.TAIDEART?.getInitItem) {
                const initItem = window.TAIDEART.getInitItem();
                // !this.isProdSite && initItem?.Id && console.debug("TAIDEART InitItem", initItem);

                // // detect PreferredLocale
                // if (initItem?.PreferredLocale) {
                //     const locale = initItem.PreferredLocale;
                //     console.debug("InitItem has PreferredLocale", locale);
                //     if (locale !== this.$i18n.locale) {
                //         // save locale to store
                //         this.$store.commit('ux/setLocale', locale);
                //         // XXX changed (was: don't emit locale-change)
                //         this.$eventBus.$emit('locale-change', locale);
                //     }
                // }

                // detect LocalUrl and replace url
                const url = initItem?.LocalUrl; // is really a path
                if (url && !window.location.pathname?.match(url)) {
                    console.debug("InitItem has LocalUrl; redirect to", url);
                    window.location.replace(url);
                    return true;
                }

                this.initItem = initItem;
                return false; // no redirect action
            }
        },

        /**
         * Called (debounced) at initial mount and when routes change.
         * Vue app is usually mounted at /, even if navigated to a subpage.
         * Debouncing this call (to trailing edge) will ensure this is called
         * once at initial mount. Otherwise this would be two times.
         *
         * The page might not require auth check, but once user navigates from
         * such page to a page that requires session, it will be loaded.
         */
        detectRoute: async function () {
            if (this.$route) {
                // UseProfile query param overrides
                this.setUseProfileByQuery(this.$route.query);
                this.setLanguageByQuery(this.$route.query);
                this.setLanguageFromInitItem();
                // check if route meta says not to load session (eg. on email check page)
                if (this.$route.meta?.noSessionCheck) {
                    console.info("No session check at ", this.$route.path);
                }
                else {
                    // init session once
                    if (!this.hasInitialSession) {
                        this.hasInitialSession = true;
                        await this.initUserSession();
                    }
                    // emit access check request
                    this.$eventBus.$emit('page-access-check');
                }
                // TODO: Matomo page view counter
                // pushTXQ(['newPageView', this.$route.path]);
            }

            this.onReady();
            return true;
        },

        /**
         * Load user profile and profile items for authenticated users.
         */
        fetchUserProfileData: async function () {
            if (this.isAuthenticated) {
                await this.$store.dispatch('user/fetchUserProfile')
                    .then((userProfile) => {

                        if (this.$store.getters['ux/getLoginPhase'] < phaseProfile) {
                            this.$store.commit('ux/setLoginPhase', phaseProfile);
                        }

                        // check for terms of service agreement
                        this.showModalTermsOfService = !!(userProfile && !userProfile.TermsOfServiceAccepted);

                        // emit signal to detect current page access level
                        this.$eventBus.$emit('page-access-check');
                        this.$eventBus.$emit('user-profile-fetched');

                        return userProfile;
                    })
                    .catch(() => {
                        console.warn("User Profile not found");
                    });


                // load profile items after a short timeout
                // (the timeout delay depends on connection speed, but is instant on UserProfile pages)
                let delay = 2500;
                if (this.$route.name?.match('UserProfile')) {
                    delay = 0;
                }
                else if (navigator?.connection?.downlink) {
                    if (navigator.connection.downlink < 0.5) {
                        delay = 7000;
                    }
                    else if (navigator.connection.downlink < 1.5) {
                        delay = 5000;
                    }
                    else if (navigator.connection.downlink < 2.5) {
                        delay = 3500;
                    }
                }

                // fetch profile items with delay just fill up the store
                const timeoutProfileItemsRef = setTimeout(async () => {
                    await this.$store.dispatch('user/fetchUserProfileItems')
                        .then(() => {
                            if (this.$store.getters['ux/getLoginPhase'] < phaseComplete) {
                                this.$store.commit('ux/setLoginPhase', phaseComplete);
                            }
                            return true;
                        })
                        .catch((err) => {
                            console.warn("User ProfileItems not found");
                            return err;
                        });

                    // console.info("ProfileItems fetch complete");

                }, delay);

                return timeoutProfileItemsRef;
            }
        },

        /**
         * User session hello returns CurrentUiTag.
         * That is saved to store, which also can check if the tag is ok, or if stores need to be cleared.
         */
        checkUiTag: function (currentUiTag) {
            return this.$store.dispatch('ux/checkUiTag', currentUiTag)
                .then(() => {
                    return true;
                })
                .catch((error) => {
                    console.info(error.message, "- clear localStorage and reload page");

                    // clear whole localStorage
                    localStorage.clear();

                    // reload client browser
                    window.location.reload();

                    return false;
                });
        },

        /**
         * Get GDPR accepted cookie
         */
        getGdprCookie: function () {
            return document?.cookie?.match('gdpr=true');
        },

        /**
         * Read UseProfile URL query param UseProfile (alias "up"), save to store.
         */
        setUseProfileByQuery: function (query) {
            if (!query || !Object.keys(query).length) {
                return;
            }
            const useProfile = query && (query.UseProfile || query.up);
            if (useProfile && useProfile !== this.$store.getters['ux/getUseProfile']) {
                // save new profile to store, emit change signal
                this.$store.commit('ux/setUseProfile', useProfile);
                this.$eventBus.$emit('use-profile-change', useProfile, true);
            }
            // console.log("App: setUseProfileByQuery", query, useProfile);

            // clear UseProfile url query param
            if (useProfile && process.client) {
                const url = window.location.href
                    .replace(/UseProfile=[^&]*/, '')
                    .replace(/[?&]up=[^&]*/, '')
                    .replace(/\?$/, '');

                // Tempering window location triggers reload,
                // but history replaceState wipes the UseProfile query from the url.
                // This does not break the back button.
                window.history.replaceState({}, document.title, url);
            }
        },

        /**
         * Url param `lang` overrides locale setting.
         */
        setLanguageByQuery: function (query) {
            const lang = query?.lang;
            if (lang && lang !== this.$i18n.locale) {
                this.$eventBus.$emit('locale-change', lang);

                // clear lang url query param
                if (process.client) {
                    const url = window.location.href
                        .replace(/lang=[^&]*/, '')
                        .replace(/\?$/, '');

                    // Tempering window location triggers reload,
                    // but history replaceState wipes the query param from the url.
                    // This does not break the back button.
                    window.history.replaceState({}, document.title, url);
                }
            }
        },

        setLanguageFromInitItem: function() {
            if (window.TAIDEART?.getInitItem) {
                const initItem = window.TAIDEART.getInitItem();

                // detect PreferredLocale
                if (initItem?.PreferredLocale) {
                    const locale = initItem.PreferredLocale;
                    console.debug("InitItem has PreferredLocale", locale);
                    if (locale !== this.$i18n.locale) {
                        // save locale to store
                       // this.$store.commit('ux/setLocale', locale);
                        // XXX changed (was: don't emit locale-change)
                        this.$eventBus.$emit('locale-change', locale);
                    }
                }
            }
        },

        /**
         * Set active theme for the App.
         * Sets the document root html class attribute (not just current #app mount point).
         * Called at mount and on `app-theme-change` bus signal.
         * If param `theme` is given, the store value is updated.
         */
        setTheme: function (theme) {
            if (theme) {
                // save theme to store
                if (this.$store.getters['ux/getTheme'] !== theme) {
                    this.$store.commit('ux/setTheme', theme);
                }
            }
            else {
                // get theme from store
                theme = this.$store.getters['ux/getTheme'];
            }

            // set document body html classes
            document.querySelector('html').setAttribute('class', `theme-${theme}`);

            // emit signal for navbar
            if (theme === 'dark') {
                this.$eventBus.$emit('style-change', {navigation: 'light'});
            }
            // else {
            //     // no signal on eventbus, would override route meta
            //     this.$eventBus.$emit('style-change', {navigation: ''});
            // }

            // set theme into cookie
            // -- this value is never read in the client - it is for the backend,
            //    to perhaps get alternate color MissingImage?
            this.$helpers.setCookie('client-app-theme', theme, 10080); // expires in a week
        },

        /**
         * Close the splash screen (when the view is ready).
         * Triggered from page-ready signal.
         * Timeout to let this page settle just a bit first.
         */
        closeSplash: function () {
            setTimeout(() => {
                const splash = document.querySelector('#splash');
                if (splash) {
                    splash.className = 'done';
                }
            }, 500);
        },

        /**
         * Triggered when received `ready` signal from the router-view.
         * All views MUST emit `ready` or `error` signal!
         */
        onReady: function () {
            if (!this.ready) {
                this.closeSplash();
                this.ready = true;
            }
        },

        /**
         * Listens to when `page-update` signal on the `eventBus`.
         */
        onUpdate: function () {
            // console.debug("App: onUpdate", this.item);
            this.updateKey += 1;
            this.error = null;
            this.$eventBus.$emit('entity-reload', this.item?.Id, true);
        },

        /**
         * All Error catching for error message modal goes through here.
         */
        onError: function (error) {
            this.error = error;
            this.ready && this.closeSplash();

            // TODO: if this is the first navigation to the site (out of the blue)
            //       then redirect to ErrorPage, or some other way provide a background image
            //       and more navigation options
        },

        /**
         * Update key index to re-render the entire view.
         */
        onRefresh: function () {
            // console.debug("App: onRefresh", this.updateKey);
            this.updateKey += 1;
        },

        /**
         * Clear url query UseProfile (or up) when profile changes
         */
        onUseProfileChange: function (useProfile) {
            // console.log("App: UseProfile changed", useProfile);
            this.onUpdate();
            this.onClearKeepAlive();
        },

        onAuthChange: function (_auth) {
            // take the value from the bus
            this.isAuthenticated = (_auth && _auth.authenticated) || false;

            // if authentication state changed
            if (this.isAuthenticated !== this.isUserSessionAuthenticated()) {

                // edit mode setting from last session is turned off for anonymous
                if (!this.isAuthenticated && this.$store.getters['ux/getEditMode']) {
                    this.$store.commit('ux/setEditMode', false);
                }

                // clear keep-alive
                this.onClearKeepAlive();

                // reset error
                this.error = null;
            }
        },

        /**
         * Page Visibility API event.
         * Triggers when tab is switched out of view and back.
         */
        onVisibilityChange: function (event) {
            // when page is hidden, keep-alive is cleared
            if (document.visibilityState === 'hidden') {
                this.keepAliveInclude = [];
            }
            // when page is shown, keep-alive is restored and user session checked
            else if (document.visibilityState === 'visible') {
                this.resetKeepAlive();
                this.checkAuthenticationState();
            }
        },

        /**
         * Clear keep-alive cache, by clearing the array
         * and reseting the components debounced.
         */
        onClearKeepAlive: function () {
            if (!this.isTestEnv) {
                this.keepAliveInclude = [];
                this.debouncedResetKeepAlive();
            }
        },

        /**
         * Resets caching, should be called debounced.
         */
        resetKeepAlive: function () {
            this.keepAliveInclude = _.clone(keepAliveComponents);
        },

        onOpenTermsModal: function () {
            this.showModalTermsOfService = true;
        },

        onAcceptTermsOfService: async function () {
            await this.$SVC.patchProfile({ TermsOfServiceAccepted: true })
                .then(() => {
                    // clear old profile from store
                    this.$store.commit('user/clearProfile');
                    this.$nextTick(() => {
                        // reload window to ensure updated profile
                        window.location.reload();
                    });
                })
                .catch((error) => {
                    this.$eventBus.$emit('save-error', error);
                });
            this.showModalTermsOfService = false;
        }
    }
};
</script>

<style lang="scss">
@import '~assets/styles/app';
</style>
